import { useTranslation } from 'react-i18next';
import { Meta } from '@/components/MetaManager';
import { PageLayout } from '@/components/PageLayout';
import { FileText, CheckCircle, AlertTriangle, Scale, Globe, Mail } from 'lucide-react';

export default function TermsOfService() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const lastUpdated = '2025-01-28';

  const sections = isZh ? [
    {
      icon: CheckCircle,
      title: '接受条款',
      content: `访问和使用本网站即表示您同意受这些服务条款的约束。如果您不同意这些条款的任何部分，请不要使用本网站。

我们保留随时修改这些条款的权利。继续使用本网站即表示您接受修改后的条款。`
    },
    {
      icon: Globe,
      title: '网站使用',
      content: `本网站提供以下内容和服务：
• 个人博客和文章
• 项目展示和案例研究
• Pearl Framework 方法论介绍
• Challenge 100 创业探索内容

您同意仅将本网站用于合法目的，不得：
• 以任何可能损害网站或干扰他人使用的方式使用本网站
• 尝试未经授权访问网站的任何部分
• 使用自动化工具大量抓取网站内容`
    },
    {
      icon: FileText,
      title: '知识产权',
      content: `本网站上的所有内容，包括但不限于：
• 文本、图像、图形、徽标
• 软件和代码
• Pearl Framework、TIE-ASM 模型等方法论
• Challenge 100 系统

均为 borninsea / 王海生 的知识产权或经授权使用。未经明确书面许可，不得复制、分发或创建衍生作品。

部分开源项目可能受其各自许可证约束，具体请参阅相关项目说明。`
    },
    {
      icon: AlertTriangle,
      title: '免责声明',
      content: `本网站内容仅供参考和教育目的：

• **非专业建议**：网站上关于创伤疗愈、身心健康的内容不构成医疗、心理或法律建议。如需专业帮助，请咨询持证专业人士。

• **信息准确性**：我们尽力确保信息准确，但不保证内容的完整性、准确性或时效性。

• **外部链接**：本网站可能包含第三方网站链接。我们不对这些外部网站的内容或隐私政策负责。

• **使用风险**：您使用本网站及其内容的风险由您自行承担。`
    },
    {
      icon: Scale,
      title: '责任限制',
      content: `在法律允许的最大范围内：

• borninsea 及其所有者不对因使用本网站而产生的任何直接、间接、附带、特殊或后果性损害承担责任。

• 这包括但不限于数据丢失、业务中断或利润损失。

• 本网站按"现状"和"可用性"提供，不提供任何明示或暗示的保证。`
    },
    {
      icon: Mail,
      title: '联系与争议解决',
      content: `如果您对这些条款有任何疑问或争议：

• 请首先通过 Twitter @wanghaisheng 或网站联系表单与我们联系
• 我们将尽力通过友好协商解决任何争议
• 本条款受中华人民共和国法律管辖

感谢您访问 borninsea.com！`
    }
  ] : [
    {
      icon: CheckCircle,
      title: 'Acceptance of Terms',
      content: `By accessing and using this Website, you agree to be bound by these Terms of Service. If you do not agree to any part of these terms, please do not use this Website.

We reserve the right to modify these terms at any time. Continued use of the Website constitutes acceptance of the modified terms.`
    },
    {
      icon: Globe,
      title: 'Website Use',
      content: `This Website provides the following content and services:
• Personal blog and articles
• Project showcases and case studies
• Pearl Framework methodology introduction
• Challenge 100 entrepreneurial exploration content

You agree to use this Website only for lawful purposes and shall not:
• Use the Website in any way that may damage it or interfere with others' use
• Attempt unauthorized access to any part of the Website
• Use automated tools to scrape Website content in bulk`
    },
    {
      icon: FileText,
      title: 'Intellectual Property',
      content: `All content on this Website, including but not limited to:
• Text, images, graphics, logos
• Software and code
• Methodologies such as Pearl Framework and TIE-ASM model
• Challenge 100 system

is the intellectual property of borninsea / Wang Haisheng or used under authorization. Reproduction, distribution, or creation of derivative works without explicit written permission is prohibited.

Some open-source projects may be subject to their respective licenses. Please refer to the relevant project documentation.`
    },
    {
      icon: AlertTriangle,
      title: 'Disclaimer',
      content: `The content on this Website is for informational and educational purposes only:

• **Not Professional Advice**: Content about trauma healing and mental health does not constitute medical, psychological, or legal advice. Please consult licensed professionals for professional help.

• **Information Accuracy**: We strive to ensure accuracy but do not guarantee the completeness, accuracy, or timeliness of the content.

• **External Links**: This Website may contain links to third-party websites. We are not responsible for the content or privacy policies of these external sites.

• **Use at Your Own Risk**: Your use of this Website and its content is at your own risk.`
    },
    {
      icon: Scale,
      title: 'Limitation of Liability',
      content: `To the maximum extent permitted by law:

• borninsea and its owner shall not be liable for any direct, indirect, incidental, special, or consequential damages arising from the use of this Website.

• This includes but is not limited to data loss, business interruption, or loss of profits.

• This Website is provided "as is" and "as available" without any express or implied warranties.`
    },
    {
      icon: Mail,
      title: 'Contact & Dispute Resolution',
      content: `If you have any questions or disputes regarding these terms:

• Please first contact us via Twitter @wanghaisheng or the website contact form
• We will endeavor to resolve any disputes through friendly negotiation
• These terms are governed by the laws of the People's Republic of China

Thank you for visiting borninsea.com!`
    }
  ];

  return (
    <PageLayout>
      <Meta
        title={isZh ? '服务条款 | borninsea' : 'Terms of Service | borninsea'}
        description={isZh ? '了解使用 borninsea.com 网站的条款和条件。' : 'Learn about the terms and conditions for using borninsea.com.'}
      />

      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold mb-4">
              {isZh ? '服务条款' : 'Terms of Service'}
            </h1>
            <p className="text-muted-foreground">
              {isZh ? '最后更新：' : 'Last Updated: '}{lastUpdated}
            </p>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
            <p className="text-lg text-muted-foreground leading-relaxed">
              {isZh 
                ? '欢迎使用 borninsea.com。以下服务条款规定了您访问和使用本网站的条件。请仔细阅读这些条款。'
                : 'Welcome to borninsea.com. The following Terms of Service govern your access to and use of this Website. Please read these terms carefully.'}
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold mb-3">{section.title}</h2>
                    <div className="text-muted-foreground whitespace-pre-line leading-relaxed">
                      {section.content.split('**').map((part, i) => 
                        i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-12 p-6 bg-muted/50 rounded-xl text-center">
            <p className="text-sm text-muted-foreground">
              {isZh 
                ? '使用本网站即表示您已阅读、理解并同意受这些服务条款的约束。'
                : 'By using this Website, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.'}
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
